var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useMemo } from "react";
import { useTheme } from "@emotion/react";
import * as pc from "playcanvas";
import * as Styled from "./cup-fill-overlay.style";
import { cupHalfExtents } from "3d/cup-box-assignment";
/* Points along each ghost's outline — enough for a smooth ellipse in
 * perspective, few enough to redo for every cup on every pointer move. */
var outlineSamples = 24;
/*
 * The world→screen projection of a point on the cup plane. Cups are drawn as
 * their real outline projected point by point rather than as a screen-space
 * circle, so they foreshorten with the view exactly like the cups themselves.
 */
var project = function (camera, point, planeY) {
    var screen = camera.worldToScreen(new pc.Vec3(point.x, planeY, point.z));
    return { x: screen.x, y: screen.y };
};
var toPath = function (points) {
    return points
        .map(function (_a, index) {
        var x = _a.x, y = _a.y;
        return "".concat(index === 0 ? "M" : "L").concat(x.toFixed(1), " ").concat(y.toFixed(1));
    })
        .join(" ") + " Z";
};
/* The outline of a copy of the anchor's cup centred at `center`. */
var cupOutlinePath = function (camera, layout, center) {
    var cup = layout.cup, planeY = layout.planeY;
    var rx = cup.width / 2;
    var rz = cup.depth / 2;
    // Rotation about Y (PlayCanvas: Y up, right-handed).
    var theta = (cup.rotationY * Math.PI) / 180;
    var cos = Math.cos(theta);
    var sin = Math.sin(theta);
    var points = [];
    for (var i = 0; i < outlineSamples; i++) {
        var t = (i / outlineSamples) * 2 * Math.PI;
        var localX = rx * Math.cos(t);
        var localZ = rz * Math.sin(t);
        points.push(project(camera, {
            x: center.x + localX * cos + localZ * sin,
            z: center.z - localX * sin + localZ * cos,
        }, planeY));
    }
    return toPath(points);
};
/*
 * The whole area the grid covers — the anchor and every cell, padded by the
 * cup's footprint — as a dashed frame, so the extent reads at a glance even
 * before the eye has counted ghosts.
 */
var areaPath = function (camera, layout, fill) {
    var cells = __spreadArray(__spreadArray([layout.anchor], __read(fill.placed), false), __read(fill.overflow), false);
    var extents = cupHalfExtents(layout.cup);
    var minX = Math.min.apply(Math, __spreadArray([], __read(cells.map(function (c) { return c.x; })), false)) - extents.x;
    var maxX = Math.max.apply(Math, __spreadArray([], __read(cells.map(function (c) { return c.x; })), false)) + extents.x;
    var minZ = Math.min.apply(Math, __spreadArray([], __read(cells.map(function (c) { return c.z; })), false)) - extents.z;
    var maxZ = Math.max.apply(Math, __spreadArray([], __read(cells.map(function (c) { return c.z; })), false)) + extents.z;
    return toPath([
        { x: minX, z: minZ },
        { x: maxX, z: minZ },
        { x: maxX, z: maxZ },
        { x: minX, z: maxZ },
    ].map(function (corner) { return project(camera, corner, layout.planeY); }));
};
/**
 * The preview of an armed fill: a ghost outline for every cup the click would
 * create, in red for the cells the grid has room for but the system does not
 * (Tacton slots / node ids), and a running count beside the pointer.
 */
export var CupFillGhosts = function (_a) {
    var camera = _a.camera, layout = _a.layout, fill = _a.fill, pointer = _a.pointer, viewVersion = _a.viewVersion;
    var theme = useTheme();
    var green = theme.colors.palette.green[500];
    var red = theme.colors.palette.red[500];
    var paths = useMemo(function () { return ({
        area: areaPath(camera, layout, fill),
        placed: fill.placed.map(function (center) {
            return cupOutlinePath(camera, layout, center);
        }),
        overflow: fill.overflow.map(function (center) {
            return cupOutlinePath(camera, layout, center);
        }),
        pointer: project(camera, pointer, layout.planeY),
    }); }, 
    // viewVersion is not read: it is what forces a re-projection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [camera, layout, fill, pointer, viewVersion]);
    var isCut = fill.overflow.length > 0;
    return (_jsxs(_Fragment, { children: [_jsxs(Styled.Ghosts, { "data-testid": "cup-fill-ghosts", "aria-hidden": true, children: [(fill.placed.length > 0 || isCut) && (_jsx("path", { d: paths.area, fill: "none", stroke: green, strokeWidth: 1, strokeDasharray: "6 4" })), paths.placed.map(function (d, index) { return (_jsx("path", { "data-ghost": "placed", d: d, fill: "".concat(green, "33"), stroke: green, strokeWidth: 1.5 }, "placed-".concat(index))); }), paths.overflow.map(function (d, index) { return (_jsx("path", { "data-ghost": "overflow", d: d, fill: "".concat(red, "1a"), stroke: red, strokeWidth: 1.5, strokeDasharray: "4 3" }, "overflow-".concat(index))); })] }), _jsxs(Styled.PointerBadge, { x: paths.pointer.x, y: paths.pointer.y, isCut: isCut, "data-testid": "cup-fill-badge", children: ["+", fill.placed.length] })] }));
};
